import React, { useState, useEffect, useContext } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    MenuItem,
    Typography,
} from '@mui/material';
import { SettingsContext } from '../../../context/SettingsContext';
import { UserRole } from '../../../models/settings';

interface User {
    id: number;
    username: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    role: string;
    createdAt: string;
    updatedAt: string;
}

interface UserFormModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (userData: Partial<User>) => void;
    user: User | null;
}

interface FormData {
    username: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    role: string;
    password?: string;
}

export const UserFormModal: React.FC<UserFormModalProps> = ({
    open,
    onClose,
    onSave,
    user,
}) => {
    const { settings } = useContext(SettingsContext);
    const [formData, setFormData] = useState<FormData>({
        username: '',
        email: '',
        role: '',
        password: '', // Only used for new users
    });

    useEffect(() => {
        console.log('UserFormModal - user prop:', user);
        if (user) {
            const newFormData = {
                username: user.username,
                email: user.email || '',
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                phoneNumber: user.phoneNumber || '',
                role: user.role,
                password: '', // Don't show password for existing users
            };
            console.log('UserFormModal - setting form data:', newFormData);
            setFormData(newFormData);
        } else {
            setFormData({
                username: '',
                email: '',
                firstName: '',
                lastName: '',
                phoneNumber: '',
                role: UserRole.RECRUITER, // Default role for new users
                password: '',
            });
        }
    }, [user]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        // Validate RECRUITER role requirements
        if (formData.role === UserRole.RECRUITER) {
            if (!formData.email || !formData.firstName || !formData.lastName || !formData.phoneNumber) {
                alert('RECRUITER role requires email, first name, last name, and phone number fields.');
                return;
            }
        }
        
        const userData = { ...formData };
        if (user) {
            // Don't send password when editing unless it's been changed
            if (!userData.password) {
                delete userData.password;
            }
        }
        onSave(userData);
    };

    const getRoleDescription = (role: string) => {
        const descriptions: Record<string, string> = {
            [UserRole.ADMIN]: 'Full system access and control',
            [UserRole.PLANNER]: 'Manage schedules and assignments',
            [UserRole.RECRUITER]: 'Handle worker recruitment and onboarding (requires contact info)',
            [UserRole.CLEANER]: 'View and manage cleaning assignments',
            [UserRole.FINANCIAL]: 'Handle financial operations and reports'
        };
        return descriptions[role] || role;
    };

    const isRecruiterRole = formData.role === UserRole.RECRUITER;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>{user ? 'Edit User' : 'Add New User'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                        <TextField
                            label="Username"
                            value={formData.username}
                            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                            required
                            fullWidth
                        />
                        <TextField
                            label="First Name"
                            value={formData.firstName}
                            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                            required={isRecruiterRole}
                            fullWidth
                        />
                        <TextField
                            label="Last Name"
                            value={formData.lastName}
                            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                            required={isRecruiterRole}
                            fullWidth
                        />
                        <TextField
                            label="Email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            required={isRecruiterRole}
                            fullWidth
                        />
                        <TextField
                            label="Phone Number"
                            value={formData.phoneNumber}
                            onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                            required={isRecruiterRole}
                            fullWidth
                        />
                        <TextField
                            select
                            label="Role"
                            value={formData.role}
                            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                            required
                            fullWidth
                            SelectProps={{
                                renderValue: (selected: unknown) => (
                                    <Box>
                                        <Typography variant="body1">{selected as string}</Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {getRoleDescription(selected as string)}
                                        </Typography>
                                    </Box>
                                )
                            }}
                        >
                            {settings?.userRoles.map((role) => (
                                <MenuItem key={role} value={role}>
                                    <Box>
                                        <Typography variant="body1">{role}</Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {getRoleDescription(role)}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </TextField>
                        {!user && (
                            <TextField
                                label="Password"
                                type="password"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                required
                                fullWidth
                            />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="submit" variant="contained" color="secondary">
                        {user ? 'Save Changes' : 'Add User'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}; 